import type { RoundCountSummary } from "@cloudnest/horror-race-engine";
import { useEffect } from "react";
import { CoinMemoryExactRow } from "./CoinMemoryExactRow";
import { CoinMemoryRow } from "./CoinMemoryRow";
import { HorrorCharacterIcon } from "./HorrorCharacterIcon";

interface CoinMemorySheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** All current-race round summaries, desc by roundNumber. */
  readonly summaries: readonly RoundCountSummary[];
  /** Latest-complete summary, or null. Drives the "★" top row. */
  readonly exactSummary: RoundCountSummary | null;
  /** Player count + race label for the header subtitle. */
  readonly playerCount: number;
  readonly raceNumber: number;
  readonly totalRaces: number;
}

const SCRIM_STYLE: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.62)",
  zIndex: 40,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
};
const SHEET_STYLE: React.CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: "480px",
  height: "86%",
  background: "var(--cn-horror-bg)",
  border: "1px solid var(--cn-horror-surface-border)",
  borderBottom: 0,
  borderRadius: "14px 14px 0 0",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 -10px 30px rgba(0, 0, 0, 0.6)",
  overflow: "hidden",
};
const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "11px 12px 9px",
  borderBottom: "1px solid var(--cn-horror-surface-border)",
  background: "var(--cn-horror-surface-secondary)",
  flex: "0 0 auto",
};
const BODY_STYLE: React.CSSProperties = {
  flex: "1 1 auto",
  overflowY: "auto",
  padding: "8px 9px 12px",
  display: "flex",
  flexDirection: "column",
  gap: "9px",
};
const TABLE_STYLE: React.CSSProperties = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  background: "var(--cn-horror-card-bg)",
  border: "1px solid var(--cn-horror-card-border)",
  borderRadius: "9px",
  overflow: "hidden",
  fontSize: "11px",
};
const TH_STYLE: React.CSSProperties = {
  padding: "6px 4px",
  textAlign: "center",
  borderBottom: "1px solid var(--cn-horror-lane-divider)",
  background: "var(--cn-horror-surface-secondary)",
  color: "var(--cn-horror-text-muted)",
  fontWeight: 700,
  fontSize: "10px",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const TH_LBL_STYLE: React.CSSProperties = {
  ...TH_STYLE,
  textAlign: "left",
  paddingLeft: "10px",
};
const TH_UNK_STYLE: React.CSSProperties = {
  ...TH_STYLE,
  fontFamily: "ui-monospace, monospace",
};

const CHARS = ["VAMPIRE", "JIANGSHI", "GUMIHO", "MUMMY", "ZOMBIE"] as const;
const CHAR_LABELS: Record<(typeof CHARS)[number], string> = {
  VAMPIRE: "뱀파이어",
  JIANGSHI: "강시",
  GUMIHO: "구미호",
  MUMMY: "미이라",
  ZOMBIE: "좀비",
};

/**
 * M1 — coin-memory sheet. Modal overlay with a 7-col sticky-header table:
 * 라운드 · 5 character icons · `?` (forfeit-unknown). Closes on X click,
 * scrim tap, or ESC. Reduced-motion safe — no enter/exit transitions
 * (jump-cut via conditional render).
 *
 * Body composition:
 *   - top "★" CoinMemoryExactRow (renders iff exactSummary non-null)
 *   - one CoinMemoryRow per current-race round, desc by roundNumber
 *   - empty-state copy when both are absent
 */
export function CoinMemorySheet({
  open,
  onClose,
  summaries,
  exactSummary,
  playerCount,
  raceNumber,
  totalRaces,
}: CoinMemorySheetProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isEmpty = summaries.length === 0 && exactSummary === null;

  return (
    <div
      data-testid="horror-coin-memory-scrim"
      style={SCRIM_STYLE}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div
        data-testid="horror-coin-memory-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="라운드 토큰 메모리"
        style={SHEET_STYLE}
      >
        <div style={HEADER_STYLE}>
          <span
            style={{
              fontSize: "13px",
              fontWeight: 800,
              color: "var(--cn-horror-text)",
            }}
          >
            코인 메모리
          </span>
          <span
            style={{
              fontSize: "9.5px",
              color: "var(--cn-horror-text-muted)",
              fontFamily: "ui-monospace, monospace",
            }}
          >
            {playerCount}인 · 레이스 {raceNumber}/{totalRaces}
          </span>
          <button
            type="button"
            data-testid="horror-coin-memory-close"
            onClick={onClose}
            aria-label="닫기"
            style={{
              marginLeft: "auto",
              background: "transparent",
              border: 0,
              color: "var(--cn-horror-text-muted)",
              fontSize: "16px",
              cursor: "pointer",
              lineHeight: 1,
              padding: "4px 8px",
            }}
          >
            ✕
          </button>
        </div>

        <div style={BODY_STYLE}>
          {isEmpty ? (
            <p
              data-testid="horror-coin-memory-empty"
              style={{
                padding: "32px 12px",
                textAlign: "center",
                color: "var(--cn-horror-text-muted)",
                fontSize: "11px",
              }}
            >
              아직 뽑힌 토큰이 없습니다
            </p>
          ) : (
            <table style={TABLE_STYLE} data-testid="round-count-table">
              <thead>
                <tr>
                  <th style={TH_LBL_STYLE}>라운드</th>
                  {CHARS.map((char) => (
                    <th key={char} style={TH_STYLE} title={CHAR_LABELS[char]}>
                      <HorrorCharacterIcon character={char} size={14} />
                    </th>
                  ))}
                  <th style={TH_UNK_STYLE} title="포기로 미공개">
                    ?
                  </th>
                </tr>
              </thead>
              <tbody>
                <CoinMemoryExactRow summary={exactSummary} />
                {summaries.map((s) => (
                  <CoinMemoryRow
                    key={`${s.raceNumber}-${s.roundNumber}`}
                    summary={s}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
